import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { ReportWidgetChartTooltip } from './ReportWidgetChartTooltip';

import type {
    ReportWidgetChartPoint,
    ReportWidgetItem,
} from '../../model/types';

interface ReportWidgetAreaChartProps {
    data: ReportWidgetChartPoint[];
    showLegend: boolean;
    tooltipFormatter: (value: number) => string;
    yAxisTickFormatter?: (value: number) => string;
    items: ReportWidgetItem[];
    total: number;
    width?: number;
    height?: number;
    disableAnimation?: boolean;
}

interface AreaDotProps {
    cx?: number;
    cy?: number;
    payload?: {
        color?: string;
    };
    strokeColor: string;
}

const ReportWidgetAreaChartDot = ({
    cx,
    cy,
    payload,
    strokeColor,
}: AreaDotProps) => {
    const dotColor = payload?.color ?? strokeColor;

    if (typeof cx !== 'number' || typeof cy !== 'number') {
        return null;
    }

    return (
        <circle
            cx={cx}
            cy={cy}
            r={3}
            fill={dotColor}
            stroke={dotColor}
            strokeWidth={1}
        />
    );
};

export const ReportWidgetAreaChart = ({
    data,
    showLegend,
    tooltipFormatter,
    yAxisTickFormatter,
    items,
    total,
    width,
    height,
    disableAnimation = false,
}: ReportWidgetAreaChartProps) => {
    const strokeColor = data[0]?.color ?? '#5ca1ff';

    return (
        <AreaChart data={data} width={width} height={height}>
            <CartesianGrid stroke="#E0E0E0" strokeWidth={1} vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} tickLine={false} />
            <YAxis
                tick={{ fontSize: 12 }}
                tickLine={false}
                tickFormatter={(v: number) => {
                    if (yAxisTickFormatter) {
                        return yAxisTickFormatter(v);
                    }

                    if (typeof v !== 'number') {
                        return String(v);
                    }
                    if (v === 0) {
                        return '0';
                    }

                    return tooltipFormatter(v);
                }}
            />
            <Tooltip
                content={
                    <ReportWidgetChartTooltip
                        tooltipFormatter={tooltipFormatter}
                        total={total}
                        items={items}
                    />
                }
            />
            {showLegend && <Legend />}
            <Area
                dataKey="value"
                name="value"
                stroke={strokeColor}
                fill={strokeColor}
                fillOpacity={0.7}
                type="monotone"
                isAnimationActive={!disableAnimation}
                dot={<ReportWidgetAreaChartDot strokeColor={strokeColor} />}
            />
        </AreaChart>
    );
};
